import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "s1", "s2", "s3", "s1Cards", "s2Cards", "s3Cards", "s3Search", "valTypeLabel",
    "reportTypeInput", "sectorInput", "propertyTypeInput",
    "searchShellTemplate", "selectedPillTemplate",
  ]

  static values = {
    sectorsByReportType: Object,
    propertyTypesBySector: Object,
    initialReportType: String,
  }

  TILE_LIMIT = 9

  #ptExtraItems = []
  #lastS3Items = []
  #selected = { rt: null, rtLabel: null, sector: null, sectorLabel: null, pt: null, ptLabel: null }

  connect() {
    if (!this.initialReportTypeValue) return

    const button = this.s1CardsTarget.querySelector(`button[data-rt-id="${CSS.escape(this.initialReportTypeValue)}"]`)
    if (button) this.selectRT({ currentTarget: button })
  }

  selectRT(event) {
    const btn = event.currentTarget
    this.#selected.rt = btn.dataset.rtId
    this.#selected.rtLabel = btn.dataset.rtLabel
    this.#selected.sector = null
    this.#selected.sectorLabel = null
    this.#selected.pt = null
    this.#selected.ptLabel = null

    this.#collapseSection(this.s1Target, btn)

    const sectors = this.#sectorOptionsFor(this.#selected.rt)
    if (sectors.length) {
      this.#renderCards(this.s2CardsTarget, sectors, "sector")
      this.#expandSection(this.s2Target)
      this.s3Target.classList.add("hidden")
    } else {
      this.#selected.sector = "n_a"
      this.#selected.pt = "n_a"
      this.s2Target.classList.add("hidden")
      this.s3Target.classList.add("hidden")
      this.#notifyComplete()
    }
    this.#syncInputs()
    this.#updateTypeLabel()
  }

  selectSector(event) {
    const btn = event.currentTarget
    this.#selected.sector = btn.dataset.sectorId
    this.#selected.sectorLabel = btn.dataset.sectorLabel
    this.#selected.pt = null
    this.#selected.ptLabel = null

    this.#collapseSection(this.s2Target, btn)

    const types = this.#propertyTypeOptionsFor(this.#selected.sector)
    if (types.length) {
      this.#lastS3Items = types
      this.#renderCards(this.s3CardsTarget, types, "pt")
      this.#expandSection(this.s3Target)
    } else {
      this.#selected.pt = "n_a"
      this.s3Target.classList.add("hidden")
      this.#notifyComplete()
    }
    this.#syncInputs()
    this.#updateTypeLabel()
  }

  selectPT(event) {
    const btn = event.currentTarget
    this.#selected.pt = btn.dataset.ptId
    this.#selected.ptLabel = btn.dataset.ptLabel
    this.#collapseSection(this.s3Target, btn)
    if (this.hasS3SearchTarget) this.s3SearchTarget.classList.add("hidden")
    this.#notifyComplete()
    this.#syncInputs()
    this.#updateTypeLabel()
  }

  // Reacts to the change the search component dispatches on its hidden input when a
  // property type is picked or cleared. The dropdown itself is teleported to the portal
  // root, but the hidden field stays inside this controller's scope.
  ptSearchChanged(event) {
    const input = event.target
    if (!input.matches || !input.matches('input[name="nqr_pt_search"]')) return
    const item = this.#ptExtraItems.find((option) => String(option.id) === String(input.value))
    if (item) {
      this.#selected.pt = item.id
      this.#selected.ptLabel = item.label
      this.#collapseS3WithSearch(item.id, item.label)
      this.#notifyComplete()
    } else {
      this.#selected.pt = null
      this.#selected.ptLabel = null
      this.#notify(false)
    }
    this.#syncInputs()
    this.#updateTypeLabel()
  }

  resetSelection() {
    this.#expandSection(this.s1Target)
    this.s2Target.classList.add("hidden")
    this.s3Target.classList.add("hidden")
    this.#selected = { rt: null, rtLabel: null, sector: null, sectorLabel: null, pt: null, ptLabel: null }
    this.#notify(false)
    this.#syncInputs()
    this.#updateTypeLabel()
  }

  editSection(event) {
    const section = event.currentTarget.dataset.section
    if (section === "s1") {
      this.resetSelection()
      return
    }
    if (section === "s2") {
      this.#expandSection(this.s2Target)
      this.s3Target.classList.add("hidden")
      this.#selected.sector = null
      this.#selected.pt = null
      this.#notify(false)
    } else if (section === "s3") {
      if (this.#lastS3Items.length) this.#renderCards(this.s3CardsTarget, this.#lastS3Items, "pt")
      this.#expandSection(this.s3Target)
      this.#selected.pt = null
      this.#selected.ptLabel = null
      this.#notify(false)
    }
    this.#syncInputs()
  }

  #renderCards(container, items, type) {
    if (type === "pt") {
      container.innerHTML = items.slice(0, this.TILE_LIMIT).map(item => this.#cardHtml(item, type)).join("")

      if (this.hasS3SearchTarget) {
        const extra = items.slice(this.TILE_LIMIT)
        if (extra.length > 0) {
          this.#ptExtraItems = extra
          this.#renderSearchInto(this.s3SearchTarget)
          this.s3SearchTarget.classList.remove("hidden")
        } else {
          this.#ptExtraItems = []
          this.s3SearchTarget.replaceChildren()
          this.s3SearchTarget.classList.add("hidden")
        }
      }
      return
    }
    container.innerHTML = items.map(item => this.#cardHtml(item, type)).join("")
  }

  #cardHtml(item, type) {
    const dataKey = `data-${type}-id="${item.id}" data-${type}-label="${item.label}"`
    const action = `click->nqr-type-selector#select${type === "sector" ? "Sector" : type === "pt" ? "PT" : "RT"}`
    return `<button type="button" class="flex items-center gap-12 p-16 rounded-12 border border-black-100 bg-white cursor-pointer hover:border-black-300 hover:bg-lightGray-1000 transition-all text-left w-full min-w-[120px] data-[selected=true]:border-brand-1000 data-[selected=true]:bg-brand-100" ${dataKey} data-action="${action}">
      <div class="flex flex-col gap-2 flex-1 min-w-0">
        <span class="text-paragraph-m font-semibold text-black-1000">${item.label}</span>
        ${item.sub ? `<span class="text-paragraph-s text-black-400">${item.sub}</span>` : ""}
      </div>
    </button>`
  }
  #renderSearchInto(target) {
    target.replaceChildren(this.searchShellTemplateTarget.content.cloneNode(true))
    requestAnimationFrame(() => {
      this.#ptSearchController(target)?.refreshOptions?.(
        this.#ptExtraItems.map((item) => ({ id: item.id, name: item.label }))
      )
    })
  }

  #ptSearchController(target) {
    const wrapper = target.querySelector('[data-controller~="single-select-field-component-with-search"]')
    return wrapper && this.application.getControllerForElementAndIdentifier(
      wrapper,
      'single-select-field-component-with-search'
    )
  }

  #collapseS3WithSearch(ptId, ptLabel) {
    this.s3CardsTarget.querySelectorAll("button[data-pt-id]").forEach(b => b.classList.add("hidden"))
    if (this.hasS3SearchTarget) {
      this.#renderSelectedPillInto(this.s3SearchTarget, ptId, ptLabel)
      this.s3SearchTarget.classList.remove("hidden")
    }
    this.s3Target.dataset.collapsed = "true"
    const changeBtn = this.s3Target.querySelector("[data-change-btn]")
    if (changeBtn) changeBtn.classList.remove("hidden")
  }

  // Clones the selected-pill markup from <template data-nqr-type-selector-target="selectedPillTemplate">.
  #renderSelectedPillInto(target, ptId, ptLabel) {
    const pill = this.selectedPillTemplateTarget.content.cloneNode(true)
    const btn = pill.querySelector("button")
    btn.dataset.ptId = ptId
    btn.dataset.ptLabel = ptLabel
    pill.querySelector("[data-pt-label-text]").textContent = ptLabel
    target.replaceChildren(pill)
  }

  #collapseSection(section, selectedBtn) {
    section.querySelectorAll("button[data-rt-id], button[data-sector-id], button[data-pt-id]").forEach(b => {
      if (b !== selectedBtn) b.classList.add("hidden")
    })
    selectedBtn.dataset.selected = "true"
    section.dataset.collapsed = "true"
    const changeBtn = section.querySelector("[data-change-btn]")
    if (changeBtn) changeBtn.classList.remove("hidden")
  }

  #expandSection(section) {
    section.querySelectorAll("button[data-rt-id], button[data-sector-id], button[data-pt-id]").forEach(b => {
      delete b.dataset.selected
      b.classList.remove("hidden")
    })
    section.dataset.collapsed = ""
    section.classList.remove("hidden")
    const changeBtn = section.querySelector("[data-change-btn]")
    if (changeBtn) changeBtn.classList.add("hidden")
    setTimeout(() => section.scrollIntoView({ behavior: "smooth", block: "nearest" }), 180)
  }

  #notifyComplete() {
    this.#notify(true)
  }

  #notify(complete) {
    this.dispatch("selectionChanged", { detail: { complete }, bubbles: true })
  }

  // Mirrors the in-memory selection into the form's hidden inputs so the values
  // POST on submit. Sector and Property Type ids arrive from the serializer
  // schema already as full Settings codes — no composing needed.
  #syncInputs() {
    if (this.hasReportTypeInputTarget) {
      this.#setValue(this.reportTypeInputTarget, this.#selected.rt || "")
    }
    if (this.hasSectorInputTarget) {
      const sector = this.#selected.sector
      this.#setValue(this.sectorInputTarget, sector && sector !== "n_a" ? sector : "")
    }
    if (this.hasPropertyTypeInputTarget) {
      const pt = this.#selected.pt
      this.#setValue(this.propertyTypeInputTarget, pt && pt !== "n_a" ? pt : "")
    }
  }

  #setValue(input, value) {
    if (input.value === value) return
    input.value = value
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Schema items arrive as { id, name }. Normalize to { id, label } so #cardHtml
  // and the search template (which expect `label`) keep working.
  #sectorOptionsFor(rtId) {
    return (this.sectorsByReportTypeValue[rtId] || []).map(o => ({ id: o.id, label: o.name }))
  }

  #propertyTypeOptionsFor(sectorId) {
    return (this.propertyTypesBySectorValue[sectorId] || []).map(o => ({ id: o.id, label: o.name }))
  }

  #updateTypeLabel() {
    const parts = [this.#selected.rtLabel, this.#selected.sectorLabel, this.#selected.ptLabel].filter(Boolean)
    const label = parts.join(" · ") || "New quote request"
    const el = document.getElementById("nqr-val-type-label")
    if (el) el.textContent = label
  }
}
